import {
    DENOMINACIONES_CLP,
    createEmptyDesglose,
    normalizeDesglose,
    sumDesglose,
    formatClp,
} from '../utils/denominacionesClp';

/**
 * Cash denomination counter (bills + coins).
 * Controlled via `value` (qty map) and `onChange(nextDesglose, total)`.
 */
export default function DenominacionEfectivo({
    value,
    onChange,
    label = 'Cantidad de billetes y monedas',
    disabled = false,
    accentColor = '#4f46e5',
    compact = false,
    /** When false, parent shows the main amount field; still shows detail sum for cross-check. */
    showTotal = true,
    hint = 'Ej.: $110.000 → 11 billetes de $10.000',
}) {
    const desglose = normalizeDesglose(value ?? createEmptyDesglose());
    const total = sumDesglose(desglose);

    const setQty = (valor, qty) => {
        const n = Math.max(0, Math.min(9999, parseInt(qty, 10) || 0));
        const next = { ...desglose, [String(valor)]: n };
        onChange?.(next, sumDesglose(next));
    };

    const bump = (valor, delta) => {
        const cur = desglose[String(valor)] || 0;
        setQty(valor, cur + delta);
    };

    const clearAll = () => {
        const empty = createEmptyDesglose();
        onChange?.(empty, 0);
    };

    const billetes = DENOMINACIONES_CLP.filter((d) => d.tipo === 'billete');
    const monedas = DENOMINACIONES_CLP.filter((d) => d.tipo === 'moneda');

    const renderRow = (d) => {
        const qty = desglose[String(d.valor)] || 0;
        const sub = d.valor * qty;
        return (
            <div
                key={d.valor}
                style={{
                    display: 'grid',
                    gridTemplateColumns: compact ? '72px 1fr 72px' : '88px 1fr 88px',
                    alignItems: 'center',
                    gap: compact ? '6px' : '8px',
                    padding: compact ? '4px 0' : '6px 0',
                    borderBottom: '1px solid #f3f4f6',
                }}
            >
                <span style={{ fontWeight: 700, fontSize: compact ? '13px' : '14px', color: '#111827' }}>
                    {d.label}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    <button
                        type="button"
                        disabled={disabled || qty <= 0}
                        onClick={() => bump(d.valor, -1)}
                        aria-label={`Menos ${d.label}`}
                        style={{
                            width: 28,
                            height: 28,
                            borderRadius: 8,
                            border: '1px solid #d1d5db',
                            background: '#fff',
                            fontWeight: 700,
                            cursor: disabled || qty <= 0 ? 'not-allowed' : 'pointer',
                            opacity: disabled || qty <= 0 ? 0.4 : 1,
                            lineHeight: 1,
                        }}
                    >
                        −
                    </button>
                    <input
                        type="number"
                        min={0}
                        max={9999}
                        inputMode="numeric"
                        disabled={disabled}
                        value={qty === 0 ? '' : qty}
                        placeholder="0"
                        onChange={(e) => setQty(d.valor, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        style={{
                            width: compact ? 48 : 56,
                            textAlign: 'center',
                            padding: '6px 4px',
                            border: '1px solid #d1d5db',
                            borderRadius: 8,
                            fontWeight: 700,
                            fontSize: '15px',
                            boxSizing: 'border-box',
                        }}
                    />
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={() => bump(d.valor, 1)}
                        aria-label={`Más ${d.label}`}
                        style={{
                            width: 28,
                            height: 28,
                            borderRadius: 8,
                            border: '1px solid #d1d5db',
                            background: '#fff',
                            fontWeight: 700,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled ? 0.4 : 1,
                            lineHeight: 1,
                        }}
                    >
                        +
                    </button>
                </div>
                <span
                    style={{
                        textAlign: 'right',
                        fontSize: compact ? '12px' : '13px',
                        fontWeight: 600,
                        color: qty > 0 ? '#111827' : '#9ca3af',
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    {formatClp(sub)}
                </span>
            </div>
        );
    };

    return (
        <div style={{ width: '100%' }}>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: 4,
                }}
            >
                <label style={{ fontSize: '13px', fontWeight: 700, color: '#374151', margin: 0 }}>
                    {label}
                </label>
                <button
                    type="button"
                    disabled={disabled || total === 0}
                    onClick={clearAll}
                    style={{
                        border: 'none',
                        background: 'transparent',
                        color: '#6b7280',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: disabled || total === 0 ? 'default' : 'pointer',
                        opacity: disabled || total === 0 ? 0.4 : 1,
                        padding: 0,
                    }}
                >
                    Limpiar
                </button>
            </div>
            {hint ? (
                <p style={{ margin: '0 0 8px', fontSize: 11, color: '#6b7280' }}>{hint}</p>
            ) : null}

            <div
                style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    padding: compact ? '8px 10px' : '10px 12px',
                    background: '#fafafa',
                    maxHeight: compact ? 280 : 340,
                    overflowY: 'auto',
                }}
            >
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
                    Billetes
                </div>
                {billetes.map(renderRow)}

                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '10px 0 2px' }}>
                    Monedas
                </div>
                {monedas.map(renderRow)}
            </div>

            {showTotal ? (
                <div
                    style={{
                        marginTop: 10,
                        padding: '10px 12px',
                        borderRadius: 12,
                        background: `${accentColor}14`,
                        border: `1px solid ${accentColor}44`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Suma del detalle</span>
                    <span
                        style={{
                            fontSize: compact ? 18 : 20,
                            fontWeight: 800,
                            color: accentColor,
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {formatClp(total)}
                    </span>
                </div>
            ) : (
                <div
                    style={{
                        marginTop: 8,
                        fontSize: 12,
                        color: '#6b7280',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <span>Suma del detalle</span>
                    <strong style={{ color: accentColor, fontVariantNumeric: 'tabular-nums' }}>
                        {formatClp(total)}
                    </strong>
                </div>
            )}
        </div>
    );
}
